import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { IRole } from "Shared/Types/role";
import {
  addRole,
  getPermissionList,
  getRolesList,
  updateRole,
} from "./reducer";
import { Role } from "Shared/Types/fleetmanagementapi";
import { MODULES, PERMISSIONS } from "Shared/Types/permissions";
import { DASHBOARD } from "Shared/Constants/routes";

type Permissions = {
  modules: Set<MODULES>;
  available_permissions: Set<PERMISSIONS>;
  has_full_access: boolean;
  role: Role | null;
  default_route: string;
};

interface InitialState {
  error?: string | null;
  loading: boolean;
  permissionsLoading: boolean;
  isFunLoading: boolean;
  success?: boolean | null;
  roles: IRole[];
  totalCount: number;
  permissions?: null | Permissions;
  defaultRoute: string;
}

const initialState: InitialState = {
  loading: false,
  permissionsLoading: false,
  isFunLoading: false,
  error: null,
  roles: [],
  totalCount: 0,
  permissions: null,
  defaultRoute: DASHBOARD,
};

const RoleSlice = createSlice({
  name: "Role",
  initialState,
  reducers: {
    clear(state) {
      state.error = null;
      state.loading = false;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRolesList.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.roles = action.payload.results;
      state.totalCount = action.payload.count;
    });
    builder.addCase(getRolesList.pending, (state, action) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getRolesList.rejected, (state, action) => {
      state.loading = false;
      state.error = action?.payload as string;
    });
    builder.addCase(getPermissionList.fulfilled, (state, action) => {
      state.permissionsLoading = false;
      state.error = null;
      state.permissions = action.payload;
      if (action.payload) {
        state.defaultRoute = action.payload.default_route;
      }
    });
    builder.addCase(getPermissionList.pending, (state, action) => {
      state.permissionsLoading = true;
      state.error = null;
    });
    builder.addCase(getPermissionList.rejected, (state, action) => {
      state.permissionsLoading = false;
      state.error = action.error.message;
    });
    builder.addCase(addRole.pending, (state) => {
      state.isFunLoading = true;
    });
    builder.addCase(addRole.fulfilled, (state) => {
      state.isFunLoading = false;
    });
    builder.addCase(addRole.rejected, (state) => {
      state.isFunLoading = false;
    });
    builder.addCase(updateRole.pending, (state) => {
      state.isFunLoading = true;
    });
    builder.addCase(updateRole.fulfilled, (state) => {
      state.isFunLoading = false;
    });
    builder.addCase(updateRole.rejected, (state) => {
      state.isFunLoading = false;
    });
  },
});

export const { clear } = RoleSlice.actions;

export const selectRoleList = (state: RootState) => state.role.roles;
export const selectRoleCount = (state: RootState) => state.role.totalCount;
export const selectRoleLoading = (state: RootState) => state.vehicle.loading;
export const selectRoleIsFuncLoading = (state: RootState) =>
  state.role.isFunLoading;
export const selectRoleError = (state: RootState) => state.vehicle.error;
export const selectRoleSuccess = (state: RootState) => state.vehicle.success;
export const selectPermissionsLoading = (state: RootState) =>
  state.role.permissionsLoading;
export const selectPermissions = (state: RootState) => state.role.permissions;
export const selectDefaultRoute = (state: RootState) => state.role.defaultRoute;

export default RoleSlice;
